import React from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import ChatColorPicker from '../common/ChatColorPicker';
import ProfileAvatar from '../common/ProfileAvatar';
import { auth } from '../../firebase/firebase-config';
import { authorColorFromUid } from '../../platform/chatAuthorColors';

function ProfileEdit({
  profile,
  savedProfile,
  isAdmin,
  units,
  profileComplete,
  pendingPhotoFile,
  photoPreviewUrl,
  saving,
  saveError,
  saveSuccess,
  showActivationMessage,
  onChange,
  onImageSelect,
  onSave,
  onCancel,
  isProfileDirty,
}) {
  const profileDisplayName = profile.displayName
    || `${profile.firstName || ''} ${profile.lastName || ''}`.trim()
    || profile.email
    || 'Uporabnik';
  const authorColor = profile.chatColor || authorColorFromUid(auth.currentUser?.uid);

  return (
    <div className="profile-subpage">
      <div className="platform-form-card">
        <div className="platform-form-card__body profile-edit">
          {!profileComplete && (
            <Alert variant="warning" className="mb-4">
              <Alert.Heading as="div" className="h5 mb-2">
                Dokončajte svoj profil
              </Alert.Heading>
              <p className="mb-0">
                Prosimo, izpolnite vse obvezne podatke profila (ime, priimek, elektronski naslov, telefon,
                delovno mesto in oddelek / področje dela), da boste lahko nadaljevali z uporabo portala.
              </p>
            </Alert>
          )}

          {showActivationMessage && !isAdmin && (
            <Alert variant="info" className="mb-4">
              Vaš profil je bil uspešno shranjen.
              V roku 24 ur bo vaš profil potrjen in boste lahko v celoti uporabljali sindikalni portal.
            </Alert>
          )}

          <div className="profile-edit__hero">
            <div
              className="profile-edit__avatar-wrap"
              style={{ '--profile-accent': authorColor }}
            >
              <ProfileAvatar
                uid={auth.currentUser?.uid}
                photoURL={photoPreviewUrl || profile.photoURL}
                displayName={profileDisplayName}
                authorColor={profile.chatColor}
                className="profile-edit__avatar"
                photoClassName="profile-edit__avatar profile-edit__avatar--photo"
                initialsClassName="profile-edit__avatar"
              />
            </div>

            <Form.Group controlId="formFile" className="profile-edit__upload mb-0">
              <Form.Label className="profile-edit__upload-label">Naloži profilno sliko</Form.Label>
              <Form.Control
                type="file"
                onChange={onImageSelect}
                accept="image/*"
                className="profile-edit__file-input"
              />
              {pendingPhotoFile && (
                <Form.Text className="profile-edit__file-name">
                  Izbrana slika: {pendingPhotoFile.name}
                </Form.Text>
              )}
            </Form.Group>
          </div>

          <Form
            className="profile-edit__form"
            onSubmit={(event) => {
              event.preventDefault();
              onSave();
            }}
          >
            <section className="profile-edit__section">
              <h3 className="profile-edit__section-title">Osnovni podatki</h3>
              <div className="profile-edit__grid">
                <Form.Group className="profile-edit__field" controlId="formFirstName">
                  <Form.Label>Ime</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Vnesite ime"
                    name="firstName"
                    value={profile.firstName}
                    onChange={onChange}
                    required
                  />
                </Form.Group>

                <Form.Group className="profile-edit__field" controlId="formLastName">
                  <Form.Label>Priimek</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Vnesite priimek"
                    name="lastName"
                    value={profile.lastName}
                    onChange={onChange}
                    required
                  />
                </Form.Group>

                <Form.Group className="profile-edit__field" controlId="formEmail">
                  <Form.Label>Elektronski naslov</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Vnesite elektronski naslov"
                    name="email"
                    value={profile.email || ''}
                    onChange={onChange}
                    required
                    autoComplete="email"
                  />
                </Form.Group>

                <Form.Group className="profile-edit__field" controlId="formPhone">
                  <Form.Label>Telefonska številka</Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="Vnesite telefonsko številko"
                    name="phone"
                    value={profile.phone}
                    onChange={onChange}
                    required
                  />
                </Form.Group>

                <p className="profile-edit__note profile-edit__field--full">
                  Sprememba elektronskega naslova zahteva potrditev preko povezave, poslane na nov naslov.
                </p>
              </div>
            </section>

            <section className="profile-edit__section">
              <h3 className="profile-edit__section-title">Organizacija</h3>
              <div className="profile-edit__grid">
                <Form.Group className="profile-edit__field" controlId="formJobPosition">
                  <Form.Label>Delovno mesto</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="npr. Referent"
                    name="jobPosition"
                    value={profile.jobPosition || ''}
                    onChange={onChange}
                    required
                  />
                </Form.Group>

                <Form.Group className="profile-edit__field" controlId="formWorkArea">
                  <Form.Label>Oddelek / področje dela</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="npr. Kadrovska služba"
                    name="workArea"
                    value={profile.workArea || ''}
                    onChange={onChange}
                    required
                  />
                </Form.Group>

                {isAdmin && (
                  <Form.Group className="profile-edit__field" controlId="formUnit">
                    <Form.Label>Enota</Form.Label>
                    <Form.Select name="unit" value={profile.unit || ''} onChange={onChange}>
                      <option value="">Izberi enoto</option>
                      {units.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.unitName}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                )}

                {isAdmin && (
                  <Form.Group className="profile-edit__field" controlId="formRole">
                    <Form.Label>Vloga</Form.Label>
                    <Form.Select name="role" value={profile.role} onChange={onChange}>
                      <option value="user">Uporabnik</option>
                      <option value="admin">Admin</option>
                    </Form.Select>
                  </Form.Group>
                )}
              </div>
            </section>

            <section className="profile-edit__section profile-edit__section--chat">
              <ChatColorPicker
                value={profile.chatColor}
                fallbackColor={authorColorFromUid(auth.currentUser?.uid)}
                onChange={(chatColor) => {
                  onChange({ target: { name: 'chatColor', value: chatColor } });
                }}
              />
            </section>

            <div className="profile-edit__actions">
              <Button
                type="submit"
                variant="primary"
                className="profile-edit__submit"
                disabled={saving || (profileComplete && !isProfileDirty())}
              >
                {saving ? 'Shranjevanje...' : 'Shrani'}
              </Button>
              {profileComplete && isProfileDirty() && (
                <Button type="button" variant="outline-secondary" onClick={onCancel} disabled={saving}>
                  Prekliči
                </Button>
              )}
            </div>

            {saveError && (
              <Alert variant="danger" className="mt-3 mb-0">
                {saveError}
              </Alert>
            )}
            {saveSuccess && !saveError && (
              <Alert variant="success" className="mt-3 mb-0">
                {saveSuccess}
              </Alert>
            )}
            {!profileComplete && pendingPhotoFile && !saveError && (
              <Form.Text className="profile-edit__pending-photo">
                Slika in podatki se shranijo ob kliku na Shrani.
              </Form.Text>
            )}
          </Form>
        </div>
      </div>
    </div>
  );
}

export default ProfileEdit;
